import { z } from 'zod';
import { personIdSchema } from '../people/person.js';
import { auditEventFieldsSchema } from '../people/audit.js';
import { SSN_LAST4_PATTERN } from './masking.js';
/**
 * The stored shapes of the sensitive store (ADR-012 §2–§3). Schemas only: the crypto and the
 * Cosmos port live in `apps/api/src/sensitive/` (#201).
 */
const base64 = z.string().min(1).regex(/^[A-Za-z0-9+/]+={0,2}$/, 'must be base64');
/**
 * One envelope-encrypted value: a fresh AES-256-GCM data key per value, wrapped by the Key
 * Vault key version named in `kid`. `kid` is metadata, not part of the GCM associated data, so
 * a re-wrap onto a new key version changes `kid` and `wrappedKey` and nothing else.
 */
export const encryptedValueSchema = z
    .object({
    alg: z.literal('A256GCM+RSA-OAEP-256'),
    /** A Key Vault key VERSION URI: `https://{vault}/keys/{name}/{version}`. */
    kid: z.string().regex(/^https:\/\/[^/\s]+\/keys\/[^/\s]+\/[^/\s]+$/, 'kid must be a Key Vault key version URI'),
    wrappedKey: base64,
    /** 96-bit GCM nonce. */
    iv: base64.length(16),
    ciphertext: base64,
    /** 128-bit GCM tag. */
    tag: base64.length(24),
})
    .strict();
export function personSensitiveId(personId) {
    return `person-sensitive:${personId}`;
}
/**
 * One document per person in the `sensitive` container, partitioned on `/personId`.
 *
 * `strict`, so a plain `ssn` string or any other extra field is refused rather than stored.
 * `ssnLast4` exists exactly when `ssn` does. `updatedBy` names an actor by type and id, never
 * by name; `revision` is for optimistic concurrency.
 */
export const personSensitiveSchema = z
    .object({
    id: z.string(),
    personId: personIdSchema,
    docType: z.literal('person-sensitive').default('person-sensitive'),
    ssn: encryptedValueSchema.optional(),
    ssnLast4: z.string().regex(SSN_LAST4_PATTERN, 'ssnLast4 must be four digits').optional(),
    dateOfBirth: encryptedValueSchema.optional(),
    updatedAt: z.string().datetime(),
    updatedBy: z
        .object({ actorType: auditEventFieldsSchema.shape.actorType.removeDefault(), actorId: z.string().min(1) })
        .strict(),
    revision: z.number().int().nonnegative(),
})
    .strict()
    .superRefine((doc, ctx) => {
    if (doc.id !== personSensitiveId(doc.personId)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['id'], message: 'id is person-sensitive:{personId}' });
    }
    if ((doc.ssn === undefined) !== (doc.ssnLast4 === undefined)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['ssnLast4'], message: 'ssnLast4 is present exactly when ssn is' });
    }
});
//# sourceMappingURL=document.js.map